import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import { Table, Button, Container, Row, Col, Card, Badge, Modal, Form, InputGroup, OverlayTrigger, Popover } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaPhone, FaWhatsapp, FaEnvelope } from 'react-icons/fa';
import './ManageJobs.css';
import BaseUrl from '../../ApiServices/BaseUrl';

function ManageJobs() {
  const [allJobs, setAllJobs] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [filteredEmployers, setFilteredEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employerCounts, setEmployerCounts] = useState({
    total: 0,
    verified: 0,
    pending: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ROWS_PER_PAGE = 10;

  // Modal state for viewing jobs
  const [showJobsModal, setShowJobsModal] = useState(false);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [selectedEmployerJobs, setSelectedEmployerJobs] = useState([]);

  // Modal state for job details
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showEditTitleModal, setShowEditTitleModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [editedJobTitle, setEditedJobTitle] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);

  const navigate = useNavigate();

  const tableRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const onMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX - tableRef.current.offsetLeft;
    scrollLeft.current = tableRef.current.scrollLeft;
    tableRef.current.style.cursor = 'grabbing';
  };
  const onMouseLeave = () => { isDragging.current = false; tableRef.current.style.cursor = 'grab'; };
  const onMouseUp = () => { isDragging.current = false; tableRef.current.style.cursor = 'grab'; };
  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - tableRef.current.offsetLeft;
    tableRef.current.scrollLeft = scrollLeft.current - (x - startX.current);
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    filterEmployers();
    setCurrentPage(1);
  }, [searchTerm, employers]);

  const fetchJobs = async () => {
    try {
      const employersResponse = await axios.get(`${BaseUrl}/api/employer/all?include_all=1`);
      const employersData = Array.isArray(employersResponse.data)
        ? employersResponse.data
        : employersResponse.data.data || [];

      if (!Array.isArray(employersData)) {
        setAllJobs([]);
        setEmployers([]);
        setFilteredEmployers([]);
        setLoading(false);
        return;
      }

      // Enrich each job with user profile details
      const enrichedJobs = employersData.map(job => {
        return {
          ...job,
          // Use the employer row data directly and avoid per-user fetches
          user_first_name: job.name || job.emp_name || 'N/A',
          user_last_name: '',
          user_email: job.email_id || 'N/A',
          user_mobile: job.phone_number || 'N/A',
          user_role: job.role || 'N/A',
          employer_type: job.employer_type || 'N/A',
          company_size: job.company_size || 'N/A',
        };
      });
      setAllJobs(enrichedJobs);

      // Group by user_id to form employer rows
      const grouped = {};
      enrichedJobs.forEach(job => {
        const uid = job.user_id || 'unknown';
        if (!grouped[uid]) {
          grouped[uid] = {
            user_id: uid,
            emp_name: job.emp_name || job.name || job.organization_name || job.user_first_name || 'N/A',
            organization_name: job.organization_name || 'N/A',
            email: job.email_id || job.user_email || 'N/A',
            phone: job.phone_number || job.user_mobile || 'N/A',
            whatsapp: job.whatsapp_number || 'N/A',
            nationality: job.nationality || 'N/A',
            user_first_name: job.user_first_name,
            user_last_name: job.user_last_name,
            user_email: job.user_email,
            user_mobile: job.user_mobile,
            employer_type: job.employer_type,
            company_size: job.company_size,
            jobs: [],
            totalJobs: 0,
            activeJobs: 0,
            pendingJobs: 0,
            isVerified: false,
            latestPosted: null
          };
        }
        grouped[uid].jobs.push(job);
        grouped[uid].totalJobs++;

        const status = (job.status || '').toLowerCase();
        if (status === 'active' || status === 'approved' || status === 'published') {
          grouped[uid].activeJobs++;
        } else {
          grouped[uid].pendingJobs++;
        }

        // Track if all jobs are verified
        const posted = job.posted_on || job.created_at;
        if (!grouped[uid].latestPosted || new Date(posted) > new Date(grouped[uid].latestPosted)) {
          grouped[uid].latestPosted = posted;
        }
      });

      // Determine verification status per employer
      const employerList = Object.values(grouped).map(emp => ({
        ...emp,
        isVerified: emp.activeJobs === emp.totalJobs && emp.totalJobs > 0
      }));

      // Sort: pending first, then by name
      employerList.sort((a, b) => {
        if (a.isVerified !== b.isVerified) return a.isVerified ? 1 : -1;
        return (a.emp_name || '').localeCompare(b.emp_name || '');
      });

      setEmployers(employerList);
      setFilteredEmployers(employerList);

      const totalEmps = employerList.length;
      const verifiedEmps = employerList.filter(e => e.isVerified).length;
      const pendingEmps = totalEmps - verifiedEmps;
      setEmployerCounts({ total: totalEmps, verified: verifiedEmps, pending: pendingEmps });

      setLoading(false);
    } catch (err) {
      console.error("Error fetching employers:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  const filterEmployers = () => {
    if (!searchTerm.trim()) {
      setFilteredEmployers(employers);
      return;
    }
    const s = searchTerm.toLowerCase().trim();
    const filtered = employers.filter(emp =>
      (emp.emp_name && emp.emp_name.toLowerCase().includes(s)) ||
      (emp.organization_name && emp.organization_name.toLowerCase().includes(s)) ||
      (emp.email && emp.email.toLowerCase().includes(s)) ||
      (emp.phone && emp.phone.includes(searchTerm)) ||
      (emp.user_first_name && emp.user_first_name.toLowerCase().includes(s)) ||
      (emp.nationality && emp.nationality.toLowerCase().includes(s))
    );
    setFilteredEmployers(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleViewJobs = (employer) => {
    setSelectedEmployer(employer);
    setSelectedEmployerJobs(employer.jobs);
    setShowJobsModal(true);
  };

  const handleCloseJobsModal = () => {
    setShowJobsModal(false);
    setSelectedEmployer(null);
    setSelectedEmployerJobs([]);
  };

  const handleViewJobCards = (employer) => {
    navigate('/postedjobsadmin', { state: { employerUserId: employer.user_id } });
  };

  const handleViewJobDetails = (job) => {
    setSelectedJob(job);
    setShowDetailsModal(true);
  };

  const handleEditJobTitle = (job) => {
    setEditingJob(job);
    setEditedJobTitle(job?.job_title || '');
    setShowEditTitleModal(true);
  };

  const handleCloseEditTitleModal = () => {
    if (savingTitle) return;
    setShowEditTitleModal(false);
    setEditingJob(null);
    setEditedJobTitle('');
  };

  const handleSaveJobTitle = async () => {
    const updatedTitle = String(editedJobTitle || '').trim();
    if (!editingJob || !updatedTitle) {
      Swal.fire('Validation', 'Job title is required.', 'warning');
      return;
    }

    try {
      setSavingTitle(true);
      await axios.put(`${BaseUrl}/api/admin/employer/${editingJob.id}/job-title`, {
        job_title: updatedTitle
      });

      setSelectedEmployerJobs((prev) =>
        prev.map((item) => (
          Number(item.id) === Number(editingJob.id)
            ? { ...item, job_title: updatedTitle }
            : item
        ))
      );

      setAllJobs((prev) =>
        prev.map((item) => (
          Number(item.id) === Number(editingJob.id)
            ? { ...item, job_title: updatedTitle }
            : item
        ))
      );

      setEmployers((prev) =>
        prev.map((emp) => ({
          ...emp,
          jobs: emp.jobs.map((item) => (
            Number(item.id) === Number(editingJob.id)
              ? { ...item, job_title: updatedTitle }
              : item
          ))
        }))
      );

      setFilteredEmployers((prev) =>
        prev.map((emp) => ({
          ...emp,
          jobs: emp.jobs.map((item) => (
            Number(item.id) === Number(editingJob.id)
              ? { ...item, job_title: updatedTitle }
              : item
          ))
        }))
      );

      if (selectedJob && Number(selectedJob.id) === Number(editingJob.id)) {
        setSelectedJob((prev) => ({ ...prev, job_title: updatedTitle }));
      }

      setShowEditTitleModal(false);
      setEditingJob(null);
      setEditedJobTitle('');

      Swal.fire({
        icon: 'success',
        title: 'Updated',
        text: 'Job title updated successfully.',
        timer: 1800,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Error updating job title:', err);
      Swal.fire(
        'Error',
        err.response?.data?.message || 'Failed to update job title.',
        'error'
      );
    } finally {
      setSavingTitle(false);
    }
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedJob(null);
  };

  const handleVerifyEmployer = async (employer) => {
    const newStatus = employer.isVerified ? 'Pending' : 'Approved';
    const action = employer.isVerified ? 'unverify' : 'verify';

    const result = await Swal.fire({
      title: `${employer.isVerified ? 'Unverify' : 'Verify'} Employer?`,
      text: `This will ${action} all ${employer.totalJobs} job posting(s) by "${employer.emp_name}".`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: employer.isVerified ? '#d33' : '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `Yes, ${action} all!`
    });

    if (!result.isConfirmed) return;

    try {
      await axios.put(`${BaseUrl}/api/verify-all-jobs/${employer.user_id}`, { status: newStatus });

      Swal.fire({
        title: 'Success!',
        text: `All ${employer.totalJobs} job(s) have been ${action === 'verify' ? 'verified' : 'unverified'}.`,
        icon: 'success',
        timer: 2000,
        timerProgressBar: true
      });

      // Refresh data
      fetchJobs();
    } catch (err) {
      console.error("Error verifying employer:", err);
      Swal.fire('Error!', err.response?.data?.message || 'Failed to update verification status.', 'error');
    }
  };

  const confirmDeleteEmployer = (employer) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `This will permanently delete all ${employer.totalJobs} job posting(s) by "${employer.emp_name}" and the associated user account. This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete permanently!',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await axios.delete(`${BaseUrl}/api/employer/user/${employer.user_id}`);
          return response.data;
        } catch (error) {
          const errorMsg = error.response?.data?.message || error.message;
          Swal.showValidationMessage(`Request failed: ${errorMsg}`);
          return null;
        }
      },
      allowOutsideClick: () => !Swal.isLoading()
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        if (result.value.success) {
          Swal.fire('Deleted!', result.value.message || 'Employer and all jobs deleted.', 'success');
          fetchJobs();
        } else {
          Swal.fire('Error!', result.value.message || 'Failed to delete.', 'error');
        }
      }
    });
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'published':
      case 'approved':
        return <Badge bg="success">Active</Badge>;
      case 'inactive':
      case 'draft':
        return <Badge bg="secondary">Draft</Badge>;
      case 'expired':
        return <Badge bg="warning">Expired</Badge>;
      case 'closed':
        return <Badge bg="danger">Closed</Badge>;
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      default:
        return <Badge bg="info">{status || 'Pending'}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatArrayField = (field) => {
    if (!field) return 'N/A';
    if (Array.isArray(field)) return field.join(', ') || 'N/A';
    return field || 'N/A';
  };

  // Phone Popover Component
  const PhonePopover = (phone, whatsapp) => (
    <Popover id="phone-popover" style={{ maxWidth: '300px' }}>
      <Popover.Header as="h3">Contact Numbers</Popover.Header>
      <Popover.Body>
        {phone && phone !== 'N/A' && (
          <div className="d-flex align-items-center mb-2 pb-2 border-bottom">
            <span className="me-2"><FaPhone className="text-secondary" /></span>
            <div>
              <small className="text-muted d-block">Phone</small>
              <a href={`tel:${phone}`} className="text-decoration-none" style={{ fontSize: '0.9rem' }}>
                {phone}
              </a>
            </div>
          </div>
        )}
        {whatsapp && whatsapp !== 'N/A' && (
          <div className="d-flex align-items-center mb-2">
            <span className="me-2"><FaWhatsapp className="text-success" /></span>
            <div>
              <small className="text-muted d-block">WhatsApp</small>
              <a href={`tel:${whatsapp}`} className="text-decoration-none" style={{ fontSize: '0.9rem' }}>
                {whatsapp}
              </a>
            </div>
          </div>
        )}
        {(!phone || phone === 'N/A') && (!whatsapp || whatsapp === 'N/A') && (
          <div className="text-muted">No contact numbers available</div>
        )}
      </Popover.Body>
    </Popover>
  );

  // Email Popover Component
  const EmailPopover = (email) => (
    <Popover id="email-popover">
      <Popover.Header as="h3">Email Address</Popover.Header>
      <Popover.Body>
        <div className="d-flex align-items-center">
          <FaEnvelope className="me-2 text-primary" />
          <a href={`mailto:${email}`} className="text-decoration-none">{email}</a>
        </div>
      </Popover.Body>
    </Popover>
  );

  if (loading) return (
    <>
      <AdminNavbar />
      <div className="text-center mt-5" style={{ marginTop: '100px' }}>Loading employers...</div>
    </>
  );

  if (error) return (
    <>
      <AdminNavbar />
      <div className="text-center mt-5 text-danger" style={{ marginTop: '100px' }}>Error: {error}</div>
    </>
  );

  return (
    <>
      <AdminNavbar />
      <Container style={{ marginTop: '80px' }}>
        <Row className="mb-4 managejobs-text">
          <Col style={{ marginTop: '20px' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2>All Employers</h2>
                <p className="text-muted">View and manage all employers and their job postings</p>
              </div>
              <div className="search-bar-container" style={{ minWidth: '300px' }}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search employers..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="search-input"
                  />
                  <InputGroup.Text>
                    <i className="bi bi-search"></i>
                  </InputGroup.Text>
                </InputGroup>
                {searchTerm && (
                  <div className="text-muted small mt-1">
                    Showing {filteredEmployers.length} of {employers.length} employers
                  </div>
                )}
              </div>
            </div>

            {/* Employer Counts */}
            <Row className="mb-4">
              <Col md={4}>
                <Card className="text-center bg-primary text-white">
                  <Card.Body>
                    <h3>{employerCounts.total}</h3>
                    <p className="mb-0">Total Employers</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="text-center bg-success text-white">
                  <Card.Body>
                    <h3>{employerCounts.verified}</h3>
                    <p className="mb-0">Verified Employers</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="text-center bg-warning text-dark">
                  <Card.Body>
                    <h3>{employerCounts.pending}</h3>
                    <p className="mb-0">Pending Employers</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

        <div className='managejobshead'>
          <Card>
            <Card.Body>
              <div className="managejobs">
                <div
                  ref={tableRef}
                  className="table-responsive"
                  onMouseDown={onMouseDown}
                  onMouseLeave={onMouseLeave}
                  onMouseUp={onMouseUp}
                  onMouseMove={onMouseMove}
                  style={{
                    overflowX: 'auto',
                    maxWidth: '100%',
                    border: '1px solid #dee2e6',
                    borderRadius: '0.375rem',
                    cursor: 'grab',
                    userSelect: 'none'
                  }}>
                  <Table striped bordered hover style={{
                    minWidth: '1000px',
                    width: '100%',
                    marginBottom: 0
                  }}>
                    <thead className="table-dark" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={{ minWidth: '60px' }}>S.No</th>
                        <th style={{ minWidth: '150px' }}>Employer Name</th>
                        <th style={{ minWidth: '100px' }}>Source</th>
                        <th style={{ minWidth: '150px' }}>Employer Type</th>
                        <th style={{ minWidth: '120px' }}>Company Size</th>
                        <th style={{ minWidth: '120px' }}>Nationality</th>
                        <th style={{ minWidth: '60px', textAlign: 'center' }}>Email</th>
                        <th style={{ minWidth: '60px', textAlign: 'center' }}>Phone</th>
                        <th style={{ minWidth: '100px' }}>Total Jobs</th>
                        <th style={{ minWidth: '100px' }}>Active</th>
                        <th style={{ minWidth: '100px' }}>Pending</th>
                        <th style={{ minWidth: '100px' }}>Status</th>
                        <th style={{ minWidth: '250px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const startIdx = (currentPage - 1) * ROWS_PER_PAGE;
                        const paginatedEmployers = filteredEmployers.slice(startIdx, startIdx + ROWS_PER_PAGE);
                        return paginatedEmployers.map((emp, index) => (
                        <tr key={emp.user_id}>
                          <td>{startIdx + index + 1}</td>
                          <td className="fw-semibold">{emp.emp_name}</td>
                          <td>
                            {emp.jobs.some(job => job.source === 'agency' || job.agency_uid) ? (
                              <Badge bg="info">Agency</Badge>
                            ) : (
                              <Badge bg="primary">Direct</Badge>
                            )}
                          </td>
                          <td>{emp.employer_type || 'N/A'}</td>
                          <td>{emp.company_size}</td>
                          <td>{emp.nationality}</td>
                          <td className="text-center">
                            {emp.email && emp.email !== 'N/A' ? (
                              <OverlayTrigger
                                trigger={['click', 'focus']}
                                placement="left"
                                overlay={EmailPopover(emp.email)}
                                rootClose
                              >
                                <button
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: '6px',
                                    cursor: 'pointer',
                                    borderRadius: '50%',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s',
                                    color: '#0d6efd'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                  title="Click to view email"
                                >
                                  <FaEnvelope size={18} />
                                </button>
                              </OverlayTrigger>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="text-center">
                            {(emp.phone && emp.phone !== 'N/A') || (emp.whatsapp && emp.whatsapp !== 'N/A') ? (
                              <OverlayTrigger
                                trigger={['click', 'focus']}
                                placement="left"
                                overlay={PhonePopover(emp.phone, emp.whatsapp)}
                                rootClose
                              >
                                <button
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: '6px',
                                    cursor: 'pointer',
                                    borderRadius: '50%',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s',
                                    color: '#28a745'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                  title="Click to view contact numbers"
                                >
                                  <FaPhone size={16} />
                                </button>
                              </OverlayTrigger>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>{emp.totalJobs}</td>
                          <td>{emp.activeJobs}</td>
                          <td>{emp.pendingJobs}</td>
                          <td>
                            {emp.isVerified
                              ? <Badge bg="success">Verified</Badge>
                              : <Badge bg="warning" text="dark">Pending</Badge>
                            }
                          </td>
                          <td>
                            <div className="d-flex gap-2 flex-nowrap">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleViewJobCards(emp)}
                                style={{ whiteSpace: 'nowrap' }}
                              >
                                <i className="bi bi-card-list me-1"></i>View Cards ({emp.totalJobs})
                              </Button>
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => handleViewJobs(emp)}
                                style={{ whiteSpace: 'nowrap' }}
                              >
                                <i className="bi bi-eye me-1"></i>View Jobs ({emp.totalJobs})
                              </Button>
                              <Button
                                variant={emp.isVerified ? 'outline-warning' : 'outline-success'}
                                size="sm"
                                onClick={() => handleVerifyEmployer(emp)}
                                style={{ whiteSpace: 'nowrap' }}
                              >
                                {emp.isVerified
                                  ? <><i className="bi bi-x-circle me-1"></i>Unverify</>
                                  : <><i className="bi bi-check-circle me-1"></i>Verify</>
                                }
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => confirmDeleteEmployer(emp)}
                                style={{ whiteSpace: 'nowrap' }}
                              >
                                <i className="bi bi-trash me-1"></i>Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ));
                      })()}
                    </tbody>
                  </Table>

                  {filteredEmployers.length === 0 && (
                    <div className="text-center py-4" style={{ minWidth: '1000px' }}>
                      {searchTerm ? (
                        <p className="text-muted">No employers found matching "<strong>{searchTerm}</strong>"</p>
                      ) : (
                        <p className="text-muted">No employers found.</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Pagination Controls */}
                {filteredEmployers.length > ROWS_PER_PAGE && (
                  <div className="mj-pagination-wrapper">
                    <button
                      className="mj-pagination-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => prev - 1)}
                    >
                      &lsaquo;
                    </button>
                    <span className="mj-pagination-info">
                      <span className="mj-pagination-current">{currentPage}</span>
                      {' / '}
                      {Math.ceil(filteredEmployers.length / ROWS_PER_PAGE)}
                    </span>
                    <button
                      className="mj-pagination-btn"
                      disabled={currentPage >= Math.ceil(filteredEmployers.length / ROWS_PER_PAGE)}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                      &rsaquo;
                    </button>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>

      {/* View Jobs Modal */}
      <Modal show={showJobsModal} onHide={handleCloseJobsModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Jobs by {selectedEmployer?.emp_name}
            {selectedEmployer?.organization_name && selectedEmployer.organization_name !== 'N/A' &&
              <small className="text-muted ms-2">({selectedEmployer.organization_name})</small>
            }
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmployerJobs.length === 0 ? (
            <p className="text-muted text-center">No jobs found for this employer.</p>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead className="table-dark">
                  <tr>
                    <th>S.No</th>
                    <th>Job Title</th>
                    <th>Job Type</th>
                    <th>Location</th>
                    <th>Salary Range</th>
                    <th>Status</th>
                    <th>Posted Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedEmployerJobs.map((job, idx) => (
                    <tr key={job.id}>
                      <td>{idx + 1}</td>
                      <td className="fw-semibold">{job.job_title || 'N/A'}</td>
                      <td>{job.job_type || 'N/A'}</td>
                      <td>
                        {job.working_city && job.state_or_province
                          ? `${job.working_city}, ${job.state_or_province}`
                          : job.working_city || job.state_or_province || 'N/A'
                        }
                      </td>
                      <td>
                        {job.minimum_monthly_salary && job.maximum_monthly_salary
                          ? `${job.currency_name || ''} ${job.minimum_monthly_salary} - ${job.maximum_monthly_salary}`
                          : 'Not specified'
                        }
                      </td>
                      <td>{getStatusBadge(job.status)}</td>
                      <td>{formatDate(job.posted_on || job.created_at)}</td>
                      <td>
                        <div className="d-flex gap-2 flex-wrap">
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handleViewJobDetails(job)}
                          >
                            View Details
                          </Button>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => handleEditJobTitle(job)}
                          >
                            Edit Title
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
      <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseJobsModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditTitleModal} onHide={handleCloseEditTitleModal} centered>
        <Modal.Header closeButton={!savingTitle}>
          <Modal.Title>Edit Job Title</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="adminEditJobTitle">
            <Form.Label>Custom title</Form.Label>
            <Form.Control
              type="text"
              value={editedJobTitle}
              onChange={(e) => setEditedJobTitle(e.target.value)}
              placeholder="Enter job title"
              autoFocus
              disabled={savingTitle}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditTitleModal} disabled={savingTitle}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleSaveJobTitle} disabled={savingTitle}>
            {savingTitle ? 'Updating...' : 'Update Title'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Job Details Modal */}
      <Modal show={showDetailsModal} onHide={handleCloseDetails} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Job Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedJob && (
            <div className="job-details-card">
              <Row>
                <Col md={6}>
                  <Card className="mb-3 fixed-height-card">
                    <Card.Header className="bg-primary text-white">
                      <h6 className="mb-0">Basic Information</h6>
                    </Card.Header>
                    <Card.Body className="card-content">
                      <p><strong>Job Title:</strong> {selectedJob.job_title}</p>
                      <p><strong>Company:</strong> {selectedJob.organization_name}</p>
                      <p><strong>Employer:</strong> {selectedJob.emp_name}</p>
                      <p><strong>Job Type:</strong> {selectedJob.job_type}</p>
                      <p><strong>Category:</strong> {selectedJob.domestic_worker_category}</p>
                      <p><strong>Status:</strong> {getStatusBadge(selectedJob.status)}</p>
                    </Card.Body>
                  </Card>

                  <Card className="mb-3 fixed-height-card">
                    <Card.Header className="bg-info text-white">
                      <h6 className="mb-0">Location & Salary</h6>
                    </Card.Header>
                    <Card.Body className="card-content">
                      <p><strong>City:</strong> {selectedJob.working_city}</p>
                      <p><strong>State/Province:</strong> {selectedJob.state_or_province}</p>
                      <p><strong>Country:</strong> {selectedJob.country}</p>
                      <p><strong>Salary Range:</strong> {selectedJob.currency_name} {selectedJob.minimum_monthly_salary} - {selectedJob.maximum_monthly_salary}</p>
                      <p><strong>Negotiable:</strong> {selectedJob.negotiable ? 'Yes' : 'No'}</p>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="mb-3 fixed-height-card">
                    <Card.Header className="bg-success text-white">
                      <h6 className="mb-0">Requirements</h6>
                    </Card.Header>
                    <Card.Body className="card-content">
                      <p><strong>Experience:</strong> {formatArrayField(selectedJob.prefer_experience)}</p>
                      <p><strong>Preferred Countries:</strong> {formatArrayField(selectedJob.preferred_candidates_country)}</p>
                      <p><strong>Languages:</strong> {formatArrayField(selectedJob.preferred_language_for_worker)}</p>
                      <p><strong>Gender:</strong> {selectedJob.gender}</p>
                      <p><strong>Religion:</strong> {selectedJob.religion}</p>
                      <p><strong>Education:</strong> {selectedJob.education_level}</p>
                      <p><strong>Age Range:</strong> {selectedJob.age}</p>
                    </Card.Body>
                  </Card>

                  <Card className="mb-3 fixed-height-card">
                    <Card.Header className="bg-warning text-dark">
                      <h6 className="mb-0">Additional Information</h6>
                    </Card.Header>
                    <Card.Body className="card-content">
                      <p><strong>Main Skills:</strong> {formatArrayField(selectedJob.main_skills)}</p>
                      <p><strong>Cooking Skills:</strong> {formatArrayField(selectedJob.cooking_skills)}</p>
                      <p><strong>Other Skills:</strong> {formatArrayField(selectedJob.other_skills)}</p>
                      <p><strong>House Type:</strong> {selectedJob.type_of_house}</p>
                      <p><strong>Rooms:</strong> {selectedJob.rooms}</p>
                      <p><strong>Bathrooms:</strong> {selectedJob.bathrooms}</p>
                      <p><strong>Adults:</strong> {selectedJob.adults}</p>
                      <p><strong>Children:</strong> {selectedJob.children}</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {selectedJob.job_description && (
                <Card className="mb-3">
                  <Card.Header className="bg-secondary text-white">
                    <h6 className="mb-0">Job Description</h6>
                  </Card.Header>
                  <Card.Body>
                    <p>{selectedJob.job_description}</p>
                  </Card.Body>
                </Card>
              )}

              <Card>
                <Card.Header className="bg-dark text-white">
                  <h6 className="mb-0">Contact Information</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Email:</strong> {selectedJob.email_id || selectedJob.user_email}</p>
                      <p><strong>Phone:</strong> {selectedJob.phone_number_country_code} {selectedJob.phone_number}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>WhatsApp:</strong> {selectedJob.whatsapp_number_country_code} {selectedJob.whatsapp_number}</p>
                      <p><strong>Nationality:</strong> {selectedJob.nationality}</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDetails}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ManageJobs;
